import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DraftPrepStaticExport,
  DraftRecommendation,
  DraftRankingBoard,
  DraftRankingPlayer,
  KeeperBoard,
  KeeperCandidate,
  TierSource,
} from '../types/draftPrep';

const POSITION_ORDER = ['QB', 'RB', 'WR', 'TE'];
const REPLACEMENT_RANKS: Record<string, number> = { QB: 20, RB: 20, WR: 30, TE: 10 };
export const POSITION_PREFERENCES = {
  QB: { leagueStarters: 20, nflStarters: 32, historicalBias: 1.1 },
  RB: { leagueStarters: 20, nflStarters: 32, historicalBias: 1 },
  WR: { leagueStarters: 30, nflStarters: 60, historicalBias: 1 },
  TE: { leagueStarters: 10, nflStarters: 32, historicalBias: 1 },
} as const;
export const KEEPER_ROSTER_RULES = {
  QB: { starterSlots: 2, extraKeeperMultiplier: 0.7 },
  RB: { starterSlots: 2, extraKeeperMultiplier: 0.7 },
  WR: { starterSlots: 3, extraKeeperMultiplier: 0.7 },
  TE: { starterSlots: 1, extraKeeperMultiplier: 0 },
} as const;
export const KEEPER_OVERRIDES = [
  {
    season: 2026,
    franchiseId: 'b2220d1a-ff75-4622-9757-09978901110f',
    playerId: 99001,
    displayPosition: 'IDP',
  },
  // Reviewed 2026 offseason trade keepers.
  {
    season: 2026,
    franchiseId: '4449259f-64af-44fd-9235-1facc0926234',
    playerId: 40055,
    displayPosition: 'RB',
  },
  {
    season: 2026,
    franchiseId: '2feaf03e-fb22-498e-ac8f-e596b6ba7810',
    playerId: 32711,
    displayPosition: 'RB',
  },
  {
    season: 2026,
    franchiseId: '2feaf03e-fb22-498e-ac8f-e596b6ba7810',
    playerId: 33500,
    displayPosition: 'WR',
  },
  {
    season: 2026,
    franchiseId: '2feaf03e-fb22-498e-ac8f-e596b6ba7810',
    playerId: 41795,
    displayPosition: 'TE',
  },
] as const;

export const getPlayerTier = (player: DraftRankingPlayer, tierSource: TierSource) => (
  tierSource === 'redraft' ? player.redraftTier : player.dynastyTier
);

export const sortPlayersByTier = (players: DraftRankingPlayer[], tierSource: TierSource) => (
  [...players].sort((left, right) => {
    const leftTier = getPlayerTier(left, tierSource) ?? Number.POSITIVE_INFINITY;
    const rightTier = getPlayerTier(right, tierSource) ?? Number.POSITIVE_INFINITY;
    return leftTier - rightTier
      || left.weightedRank - right.weightedRank
      || left.name.localeCompare(right.name);
  })
);

const MAX_STARTER_PRESSURE = Math.max(...Object.values(POSITION_PREFERENCES)
  .map(({ leagueStarters, nflStarters }) => leagueStarters / nflStarters));

const positionPreferenceMultiplier = (position: string, redraftTier?: number) => {
  const preference = POSITION_PREFERENCES[position as keyof typeof POSITION_PREFERENCES];
  if (!preference) return 1;
  const scarcityMultiplier = (preference.leagueStarters / preference.nflStarters) / MAX_STARTER_PRESSURE;
  const standardMultiplier = scarcityMultiplier * preference.historicalBias;
  if (position === 'TE') {
    return redraftTier === 1
      ? Math.max(0.9, standardMultiplier)
      : standardMultiplier * 0.5;
  }
  return standardMultiplier;
};

export const rankDraftRecommendations = (
  players: DraftRankingPlayer[],
  rosterPositions: string[],
  limit = 15,
  tierSource: TierSource = 'redraft',
): DraftRecommendation[] => {
  const rosterCounts = rosterPositions.reduce<Record<string, number>>((counts, position) => {
    counts[position] = (counts[position] || 0) + 1;
    return counts;
  }, {});
  const activeTierByPosition = new Map<string, number>();
  const remainingByPositionTier = new Map<string, number>();
  const hasOpenStarterSlots = POSITION_ORDER.some((position) => {
    const rule = KEEPER_ROSTER_RULES[position as keyof typeof KEEPER_ROSTER_RULES];
    return (rosterCounts[position] || 0) < rule.starterSlots;
  });
  players.forEach((player) => {
    const tier = getPlayerTier(player, tierSource);
    if (tier == null) return;
    const currentActiveTier = activeTierByPosition.get(player.position);
    if (currentActiveTier == null || tier < currentActiveTier) activeTierByPosition.set(player.position, tier);
    const tierKey = `${player.position}:${tier}`;
    remainingByPositionTier.set(tierKey, (remainingByPositionTier.get(tierKey) || 0) + 1);
  });

  const recommendations = players.map((player): DraftRecommendation => {
    const rule = KEEPER_ROSTER_RULES[player.position as keyof typeof KEEPER_ROSTER_RULES];
    const rosterCount = rosterCounts[player.position] || 0;
    const starterSlots = rule?.starterSlots || 0;
    const openStarterSlots = Math.max(0, starterSlots - rosterCount);
    const fillsStarterSlot = openStarterSlots > 0;
    const replacementRank = REPLACEMENT_RANKS[player.position] || 20;
    const tier = getPlayerTier(player, tierSource);
    const positionValue = 100 * (replacementRank + 1 - player.weightedRank) / replacementRank
      * positionPreferenceMultiplier(player.position, tier);
    const starterNeedBonus = fillsStarterSlot
      ? 25 + 10 * (openStarterSlots / Math.max(1, starterSlots))
      : 0;
    const depthMultiplier = fillsStarterSlot ? 1 : (rule?.extraKeeperMultiplier ?? 0.5);
    const depthPenalty = !fillsStarterSlot && depthMultiplier === 0 ? 15 : 0;
    const isActiveTier = tier != null && activeTierByPosition.get(player.position) === tier;
    const remainingInTier = tier == null ? undefined : remainingByPositionTier.get(`${player.position}:${tier}`);
    const tierUrgencyBonus = isActiveTier && remainingInTier && (fillsStarterSlot || !hasOpenStarterSlots)
      ? round(18 / remainingInTier, 1)
      : 0;
    const draftScore = round(
      positionValue * depthMultiplier + starterNeedBonus + tierUrgencyBonus - depthPenalty,
      1,
    );
    return {
      player,
      draftScore,
      fillsStarterSlot,
      rosterCount,
      starterSlots,
      tier,
      remainingInTier,
      isActiveTier,
      tierUrgencyBonus,
    };
  });

  return recommendations
    .sort((left, right) => right.draftScore - left.draftScore
      || Number(right.fillsStarterSlot) - Number(left.fillsStarterSlot)
      || left.player.weightedRank - right.player.weightedRank
      || POSITION_ORDER.indexOf(left.player.position) - POSITION_ORDER.indexOf(right.player.position)
      || left.player.name.localeCompare(right.player.name))
    .slice(0, limit);
};

const rosterAdjustedKeeperScore = (player: KeeperCandidate, selectedAtPosition: number) => {
  if (player.keeperScore == null) return Number.NEGATIVE_INFINITY;
  const rule = KEEPER_ROSTER_RULES[player.position as keyof typeof KEEPER_ROSTER_RULES];
  if (!rule || selectedAtPosition < rule.starterSlots) return player.keeperScore;
  if (rule.extraKeeperMultiplier === 0) return Number.NEGATIVE_INFINITY;
  const extraSlotNumber = selectedAtPosition - rule.starterSlots + 1;
  const multiplier = rule.extraKeeperMultiplier ** extraSlotNumber;
  return player.keeperScore >= 0
    ? player.keeperScore * multiplier
    : player.keeperScore / multiplier;
};

const selectLikelyKeepers = (
  candidates: KeeperCandidate[],
  keeperCount: number,
  forcedPlayerIds: ReadonlySet<number>,
) => {
  const eligibleForcedPlayers = candidates.filter((player) => forcedPlayerIds.has(player.playerId));
  const selected = new Set<number>(eligibleForcedPlayers.map((player) => player.playerId));
  const selectedByPosition: Record<string, number> = {};
  eligibleForcedPlayers.forEach((player) => {
    selectedByPosition[player.position] = (selectedByPosition[player.position] || 0) + 1;
  });
  while (selected.size < keeperCount) {
    const next = candidates
      .filter((player) => !selected.has(player.playerId) && player.keeperScore != null)
      .map((player) => ({
        player,
        rosterScore: rosterAdjustedKeeperScore(player, selectedByPosition[player.position] || 0),
      }))
      .filter(({ rosterScore }) => Number.isFinite(rosterScore))
      .sort((left, right) => right.rosterScore - left.rosterScore
        || (right.player.keeperScore ?? Number.NEGATIVE_INFINITY) - (left.player.keeperScore ?? Number.NEGATIVE_INFINITY)
        || left.player.name.localeCompare(right.player.name))[0];
    if (!next) break;
    selected.add(next.player.playerId);
    selectedByPosition[next.player.position] = (selectedByPosition[next.player.position] || 0) + 1;
  }
  return selected;
};

const clampWeight = (weight: number) => Math.min(0.5, Math.max(0, weight));
const round = (value: number, places: number) => {
  const multiplier = 10 ** places;
  return Math.round(value * multiplier) / multiplier;
};

const fetchDraftPrepExport = async (season: number): Promise<DraftPrepStaticExport> => {
  const response = await fetch(`/data/draft-prep/${season}.json`);
  if (!response.ok) {
    throw new Error(response.status === 404
      ? `No static draft-prep export exists for ${season}`
      : `Static draft-prep export failed (${response.status})`);
  }
  return response.json();
};

const buildRankingBoard = (source: DraftPrepStaticExport, requestedWeight: number): DraftRankingBoard => {
  const dynastyWeight = clampWeight(requestedWeight);
  const redraftWeight = 1 - dynastyWeight;
  const players: DraftRankingPlayer[] = source.players.map((player) => {
    const weightedRank = player.dynastyRank == null
      ? player.redraftRank
      : player.redraftRank * redraftWeight + player.dynastyRank * dynastyWeight;
    return {
      ...player,
      weightedRank: round(weightedRank, 2),
      dynastyOffset: round(player.redraftRank - weightedRank, 2),
    };
  }).sort((left, right) => {
    const positionDifference = POSITION_ORDER.indexOf(left.position) - POSITION_ORDER.indexOf(right.position);
    return positionDifference || left.weightedRank - right.weightedRank || left.name.localeCompare(right.name);
  });

  return {
    season: source.season,
    redraftWeight,
    dynastyWeight,
    formula: source.formula,
    unmatchedRows: source.unmatchedRows,
    snapshots: source.snapshots,
    players,
  };
};

export const buildKeeperBoard = (source: DraftPrepStaticExport, requestedWeight: number): KeeperBoard => {
  const rankings = buildRankingBoard(source, requestedWeight);
  const rankingByPlayer = new Map(rankings.players
    .filter((player) => player.playerId != null)
    .map((player) => [player.playerId as number, player]));
  const teams = source.teams.map((team) => {
    const overrides = KEEPER_OVERRIDES.filter((override) => override.season === source.season
      && override.franchiseId === team.franchiseId);
    const overrideByPlayer = new Map<number, (typeof KEEPER_OVERRIDES)[number]>(
      overrides.map((override) => [override.playerId, override]),
    );
    const candidateRosterPlayers = [...team.players];
    overrides.forEach((override) => {
      if (candidateRosterPlayers.some((player) => player.playerId === override.playerId)) return;
      const ranking = rankingByPlayer.get(override.playerId);
      if (!ranking) return;
      candidateRosterPlayers.push({
        playerId: override.playerId,
        name: ranking.name,
        position: override.displayPosition,
        age: ranking.age,
        headshotUrl: ranking.headshotUrl,
      });
    });
    const candidates: KeeperCandidate[] = candidateRosterPlayers.map((rosterPlayer): KeeperCandidate => {
      const ranking = rankingByPlayer.get(rosterPlayer.playerId);
      const keeperOverride = overrideByPlayer.get(rosterPlayer.playerId);
      const replacementRank = ranking ? REPLACEMENT_RANKS[ranking.position] : undefined;
      const keeperScore = ranking && replacementRank
        ? round(
          100 * (replacementRank + 1 - ranking.weightedRank) / replacementRank
            * positionPreferenceMultiplier(ranking.position, ranking.redraftTier),
          1,
        )
        : undefined;
      return {
        playerId: rosterPlayer.playerId,
        name: rosterPlayer.name,
        position: keeperOverride?.displayPosition ?? rosterPlayer.position,
        nflTeam: ranking?.nflTeam,
        age: ranking?.age ?? rosterPlayer.age,
        headshotUrl: ranking?.headshotUrl ?? rosterPlayer.headshotUrl,
        redraftRank: ranking?.redraftRank,
        redraftTier: ranking?.redraftTier,
        dynastyRank: ranking?.dynastyRank,
        weightedRank: ranking?.weightedRank,
        dynastyOffset: ranking?.dynastyOffset,
        keeperScore,
        category: 'Depth',
        isLikelyKeeper: false,
        isKeeperOverride: Boolean(keeperOverride),
      };
    }).sort((left, right) => {
      const leftRanked = left.keeperScore != null ? 1 : 0;
      const rightRanked = right.keeperScore != null ? 1 : 0;
      return rightRanked - leftRanked
        || (right.keeperScore ?? Number.NEGATIVE_INFINITY) - (left.keeperScore ?? Number.NEGATIVE_INFINITY)
        || left.name.localeCompare(right.name);
    });
    const likelyKeeperIds = selectLikelyKeepers(
      candidates,
      5,
      new Set(overrides.map((override) => override.playerId)),
    );
    const categorized = candidates.map((player): KeeperCandidate => {
      const isLikelyKeeper = likelyKeeperIds.has(player.playerId);
      return {
        ...player,
        isLikelyKeeper,
        category: isLikelyKeeper
          ? 'Likely keeper'
          : (player.keeperScore ?? 0) > 0 ? 'In consideration' : 'Depth',
      };
    });
    return { ...team, players: categorized };
  });

  return {
    season: source.season,
    rosterSeason: source.rosterSeason,
    redraftWeight: rankings.redraftWeight,
    dynastyWeight: rankings.dynastyWeight,
    formula: 'keeper score combines weighted rank, positional scarcity, historical preference, roster fit, and reviewed season-specific keeper overrides; projected sets allow at most one TE and apply diminishing returns beyond 2 QB, 2 RB, or 3 WR',
    teams,
  };
};

const useDraftPrepExport = (season: number) => useQuery({
  queryKey: ['draft-prep-static', season],
  queryFn: () => fetchDraftPrepExport(season),
  staleTime: Infinity,
});

export const useDraftRankings = (season: number, dynastyWeight: number) => {
  const query = useDraftPrepExport(season);
  const data = useMemo(
    () => query.data ? buildRankingBoard(query.data, dynastyWeight) : undefined,
    [query.data, dynastyWeight],
  );
  return { ...query, data };
};

export const useKeeperOptions = (season: number, dynastyWeight: number) => {
  const query = useDraftPrepExport(season);
  const data = useMemo(
    () => query.data ? buildKeeperBoard(query.data, dynastyWeight) : undefined,
    [query.data, dynastyWeight],
  );
  return { ...query, data };
};
